import { StrictOmit } from '@crossbeam/types';

import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import {
  ALL_PARTNERS_TYPE,
  CUSTOM_TYPE,
  ECOSYSTEM,
  GREENFIELD_CONSOLIDATED_TYPES,
  GREENFIELD_TYPE,
  OVERLAPS,
  PARTNER_TAGS_TYPE,
  POTENTIAL_REVENUE_TYPE,
  SINGLE_OVERLAPS_TYPE,
} from '@/constants/reports';
import { CREATOR, OWNER } from '@/constants/shared_list';
import {
  useCollaborateStore,
  usePartnersStore,
  usePopulationsStore,
  useReportsStore,
  useTeamStore,
} from '@/stores';
import { ReportCell, ReportFolder, TrimmedReport } from '@/types/reports';
import { User } from '@/types/root';
import { SharedList } from '@/types/shared_lists';

import useAuth from '../useAuth';

export function useCombinedLists(folderId?: string | null) {
  const reportsStore = useReportsStore();
  const { reports, ready: reportsReady, folders } = storeToRefs(reportsStore);
  const folder = computed(() =>
    folders.value.find((folder) => folder.id === folderId),
  );
  const sharedListsStore = useCollaborateStore();
  const {
    lists,
    ready: sharedListsReady,
    listAccess,
  } = storeToRefs(sharedListsStore);
  const { currentUser, currentOrg } = useAuth();
  const {
    partnerOrgsLookup,
    partnerOrgs,
    ready: partnersReady,
  } = storeToRefs(usePartnersStore());
  const {
    authorizations,
    revokedUsers,
    ready: teamReady,
  } = storeToRefs(useTeamStore());
  const populationsStore = usePopulationsStore();
  const { ready: populationsReady } = storeToRefs(populationsStore);
  const allReady = computed(
    () =>
      reportsReady.value &&
      sharedListsReady.value &&
      partnersReady.value &&
      teamReady.value &&
      populationsReady.value,
  );

  type TruncatedReportCell = StrictOmit<
    ReportCell,
    'hasWritePermissions' | 'trialOver' | 'isOnReportsPage' | 'hideExport'
  >;

  const reportsByOwner = computed(() =>
    reports.value.reduce(
      (
        acc: {
          yourReports: TrimmedReport[];
          sharedWithYou: TrimmedReport[];
        },
        report: TrimmedReport,
      ) => {
        if (report.created_by_user_id === currentUser.value?.id) {
          acc.yourReports.push(report);
        } else {
          acc.sharedWithYou.push(report);
        }
        return acc;
      },
      {
        yourReports: [],
        sharedWithYou: [],
      },
    ),
  );

  function getSharedListOwner(list: SharedList) {
    return sharedListsStore.getOwnerByListId(list.id);
  }
  const sharedListsByOwner = computed(() =>
    lists.value.reduce(
      (
        acc: {
          yourLists: SharedList[];
          sharedWithYou: SharedList[];
        },
        list: SharedList,
      ) => {
        if (getSharedListOwner(list)?.user_id === currentUser.value?.id) {
          acc.yourLists.push(list);
        } else {
          acc.sharedWithYou.push(list);
        }
        return acc;
      },
      {
        yourLists: [],
        sharedWithYou: [],
      },
    ),
  );

  const combinedListsByOwner = computed(() => {
    return {
      yourLists: [
        ...reportsByOwner.value.yourReports,
        ...sharedListsByOwner.value.yourLists,
      ],
      sharedWithYou: [
        ...reportsByOwner.value.sharedWithYou,
        ...sharedListsByOwner.value.sharedWithYou,
      ],
    };
  });

  const hasExternallySharedLists = computed(() =>
    combinedListsByOwner.value.sharedWithYou.some((list) => {
      if ('permissions' in list) {
        const creatorOrg = list.permissions?.find(
          (org) => org.org_access === CREATOR,
        );
        return (
          !!creatorOrg && creatorOrg.organization_id !== currentOrg.value.id
        );
      }
      if ('user_access' in list) {
        return list.shared_partner_org_id === currentOrg.value?.id;
      }
      return false;
    }),
  );

  function isOneToOne({
    our_population_ids: ourPopulationIds,
    partner_population_ids: partnerPopulationIds,
  }: {
    our_population_ids: number[];
    partner_population_ids: number[];
  }) {
    return ourPopulationIds.length === 1 && partnerPopulationIds.length === 1;
  }
  function getPartnerOrgsFromPopulations(report: TrimmedReport) {
    if (!report.partner_population_ids?.length) return [];
    const orgIds = new Set<number>();
    for (const popId of report.partner_population_ids) {
      const population = populationsStore.getPartnerPopulationById(popId);
      if (population?.organization_id) {
        orgIds.add(population.organization_id);
      }
    }
    return Array.from(orgIds)
      .map((orgId) => partnerOrgsLookup.value[orgId])
      .filter((org) => !!org)
      .sort((a, b) => a?.name?.localeCompare(b?.name || '') || 0);
  }
  function getReportType(report: TrimmedReport) {
    const flow = report.consolidated_report_type;
    if (GREENFIELD_CONSOLIDATED_TYPES.includes(flow)) return GREENFIELD_TYPE;
    if (flow === ECOSYSTEM) {
      if (report.tag_id || report.tag_ids?.length) return PARTNER_TAGS_TYPE;
      if (report.partner_population_ids?.length) {
        const reportPartnerOrgs = getPartnerOrgsFromPopulations(report);
        if (reportPartnerOrgs.length !== partnerOrgs.value.length) {
          return CUSTOM_TYPE;
        }
      }
      return ALL_PARTNERS_TYPE;
    }
    if (flow === OVERLAPS)
      return isOneToOne(report) ? SINGLE_OVERLAPS_TYPE : CUSTOM_TYPE;
    return POTENTIAL_REVENUE_TYPE;
  }
  function getPartnerOrgs(report: TrimmedReport) {
    if (report.partner_segment_filters?.items?.length) {
      const hasWildcard = report.partner_segment_filters.items.some(
        (item) => !('organization_id' in item),
      );
      if (hasWildcard) return partnerOrgs.value;
      return report.partner_segment_filters.items
        .reduce<number[]>((acc, item) => {
          if ('organization_id' in item && !acc.includes(item.organization_id))
            acc.push(item.organization_id);
          return acc;
        }, [])
        .map((orgId) => partnerOrgsLookup.value[orgId])
        .filter((org) => !!org);
    }
    if (report.partner_population_ids?.length) {
      return getPartnerOrgsFromPopulations(report);
    }
    const orgs = report.partner_organization_ids.map(
      (orgId: number) => partnerOrgsLookup.value[orgId],
    );
    return orgs.filter((org) => !!org);
  }
  function getSharedListPartnerOrgs(sharedList: SharedList) {
    if (!sharedList.permissions?.length) return [];
    const partnerOrgIds = sharedList.permissions
      .filter((perm) => perm.organization_id !== currentOrg.value.id)
      .map((perm) => perm.organization_id);
    return partnerOrgIds
      .map((orgId) => partnerOrgsLookup.value[orgId])
      .filter((org) => !!org);
  }
  function getAuths(id: number) {
    const auth = authorizations.value.find(
      (authorization) => authorization.user.id === id,
    );
    const revokedAuth = revokedUsers.value.find(
      (revokedUser) => revokedUser.id === id,
    );
    return auth
      ? auth.user
      : revokedAuth
        ? {
            ...revokedAuth,
            last_name: `${revokedAuth.last_name} (Deactivated)`,
          }
        : { first_name: 'Admin', last_name: '' };
  }
  function getReportUsers(userData: {
    updatedByUserId: number;
    createdByUserId: number;
  }): {
    updatedByUser: User;
    createdByUser: User;
  } {
    return {
      updatedByUser: getAuths(userData.updatedByUserId) as User,
      createdByUser: getAuths(userData.createdByUserId) as User,
    };
  }
  function getListOwner(list: SharedList) {
    const creatingOrg = list.permissions?.find(
      (org) => org.org_access === CREATOR,
    );
    return creatingOrg?.users?.find((user) => user.user_access === OWNER);
  }
  function getCellInfo(
    rowItem: SharedList | TrimmedReport | ReportFolder,
  ): TruncatedReportCell {
    // we're going to differentiate between shared lists and reports using
    // "user access" (reports) vs "permissions" (shared lists)
    if ('permissions' in rowItem) {
      const sharedList = rowItem as SharedList;
      const { updatedByUser, createdByUser } = getReportUsers({
        updatedByUserId: sharedList.last_updated_by_user?.id || 0,
        createdByUserId: getListOwner(sharedList)?.user_id || 0,
      });
      return {
        report: sharedList,
        partnersInReport: getSharedListPartnerOrgs(sharedList),
        reportLink: {
          name: 'collaborate_list',
          params: { id: sharedList.id },
        },
        reportType: 'single-partner-overlaps',
        updatedByUser,
        createdByUser,
        isStaticList: true,
        isFolder: false,
      };
    } else if ('user_access' in rowItem) {
      const report = rowItem as TrimmedReport;
      const { updatedByUser, createdByUser } = getReportUsers({
        updatedByUserId: report.updated_by_user_id,
        createdByUserId: report.created_by_user_id,
      });
      return {
        report,
        partnersInReport: getPartnerOrgs(report),
        reportLink: {
          name: 'edit_report',
          params: { report_id: report.id },
        },
        reportType: getReportType(report),
        updatedByUser,
        createdByUser,
        isStaticList: false,
        isFolder: false,
      };
    }
    // folder
    const folder = rowItem as ReportFolder;
    return {
      report: folder,
      isStaticList: false,
      isFolder: true,
      partnersInReport: [],
      reportLink: {
        name: 'folder',
        params: { folder_id: folder.id },
      },
      updatedByUser: null,
      createdByUser: null,
    };
  }

  return {
    folders,
    reportsStore,
    combinedListsByOwner,
    hasExternallySharedLists,
    allReady,
    getCellInfo,
    folder,
    reportsByOwner,
    partnerOrgs,
    authorizations,
    listAccess,
  };
}
